import React from 'react';
import { images } from '../constants/images';
import { Page, TranslationKey } from './pages-types';
import { PageRoutes } from './page-routes';
import { useTranslation } from 'react-i18next';
import { Link, useRouteLoaderData } from '@remix-run/react';
import {
  AccommodationContent,
  INCLUDED_AMENITIES,
  OPTIONAL_SERVICES,
  MUDHOUSE_HIGHLIGHTS,
  TENT_HIGHLIGHTS,
  DORMITORY_HIGHLIGHTS,
} from '../components/accommodation-content';
import { displayPrice } from '../state/prices';
import { loader as rootLoader } from '../root';
import { getOfferingFullRoomPrice } from '../types/offerings';
import { getAccommodation } from '../constants/accommodation';

// Dynamic content component that uses offerings data
const MudhouseContent: React.FC = () => {
  const data = useRouteLoaderData<typeof rootLoader>('root');
  const offerings = data?.offerings ?? [];

  // Find the mudhouse offering by slug
  const mudhouseOffering = offerings.find((o) => o.slug === 'mudhouse');

  const priceData = getOfferingFullRoomPrice(mudhouseOffering);
  const capacity = priceData?.capacity ?? 2;
  const pricePerNight = priceData?.amount ?? 12500; // Fallback (full room price)

  const mudhouseConfig = getAccommodation('mudhouse');

  return (
    <AccommodationContent
      priceDisplay={displayPrice({ priceInINR: pricePerNight, showCurrency: true })}
      capacity={capacity}
      highlights={MUDHOUSE_HIGHLIGHTS}
      accommodationType="mudhouse"
      cta="I'm Interested"
      galleryImages={mudhouseConfig.gallery}
      galleryAlt="Sanskriti Vann Kutiram (Mudhouses) at Kumbh Sukrit"
      description={
        <>
          <p>
            Our <strong>Sanskriti Vann Kutiram (Mudhouses)</strong> represent the pinnacle
            of eco-luxury at Kumbh Sukrit. These premium accommodations blend authentic
            architectural heritage with modern comforts, providing an unparalleled
            experience during your Magh Mela journey.
          </p>
          <p className="mt-4">
            Constructed using traditional techniques and natural materials, each mudhouse
            maintains a comfortable temperature naturally—staying cool during the day and
            warm at night. The thick earthen walls create a serene, peaceful atmosphere
            perfect for meditation and reflection.
          </p>
          <p className="mt-4">
            Inside, you&apos;ll find luxurious bedding, private bathroom facilities, and
            thoughtful amenities that honor both tradition and comfort. Large windows
            offer views of the sacred landscape while maintaining your privacy.
          </p>
        </>
      }
      amenities={INCLUDED_AMENITIES}
      optionalServices={OPTIONAL_SERVICES}
    />
  );
};

const MudhouseDormitoryContent: React.FC = () => {
  const data = useRouteLoaderData<typeof rootLoader>('root');
  const offerings = data?.offerings ?? [];

  // Find the mudhouse dormitory offering by slug
  const dormOffering = offerings.find((o) => o.slug === 'mudhouse-dormitory');

  const priceData = getOfferingFullRoomPrice(dormOffering) as {
    amount: number;
    capacity: number;
  };
  const capacity = priceData.capacity;
  const pricePerNight = priceData.amount;

  const dormConfig = getAccommodation('mudhouse-dormitory');

  return (
    <AccommodationContent
      priceDisplay={displayPrice({ priceInINR: pricePerNight, showCurrency: true })}
      capacity={capacity}
      highlights={DORMITORY_HIGHLIGHTS}
      accommodationType="mudhouse-dormitory"
      cta="I'm Interested"
      galleryImages={dormConfig.gallery}
      galleryAlt="Sanskriti Vann Sangham (Dormitory) at Kumbh Sukrit"
      description={
        <>
          <p>
            Our <strong>Sanskriti Vann Sangham (Dormitory)</strong> offers an affordable
            yet authentic accommodation option for solo travelers and groups seeking the
            traditional Kumbh experience. Built using the same eco-friendly techniques as
            our premium mudhouses, these shared spaces provide excellent value without
            compromising on the essence of traditional living.
          </p>
          <p className="mt-4">
            Each dormitory features multiple comfortable beds in a spacious earthen
            structure. The natural clay walls maintain a pleasant temperature throughout
            the day, creating an ideal environment for rest and community.
          </p>
          <p className="mt-4">
            Shared bathroom facilities are conveniently located nearby, and all guests
            enjoy the same high-quality meals and spiritual activities included in our
            other packages. Perfect for pilgrims who wish to connect with fellow travelers
            while experiencing the sacred atmosphere of Magh Mela.
          </p>
        </>
      }
      amenities={INCLUDED_AMENITIES}
      optionalServices={OPTIONAL_SERVICES}
    />
  );
};

const PremiumTwinTentContent: React.FC = () => {
  const data = useRouteLoaderData<typeof rootLoader>('root');
  const offerings = data?.offerings ?? [];

  // Find the twin tent offering by slug
  const tentOffering = offerings.find((o) => o.slug === 'premium-twin-bed-cottage');

  const priceData = getOfferingFullRoomPrice(tentOffering);
  const capacity = priceData?.capacity ?? 2;
  const pricePerNight = priceData?.amount ?? 10000; // Fallback (full room price)

  const tentConfig = getAccommodation('premium-twin-bed-cottage');

  return (
    <AccommodationContent
      priceDisplay={displayPrice({ priceInINR: pricePerNight, showCurrency: true })}
      capacity={capacity}
      highlights={TENT_HIGHLIGHTS}
      accommodationType="premium-twin-bed-cottage"
      cta="I'm Interested"
      galleryImages={tentConfig.gallery}
      galleryAlt="Sanskriti Vann Mandapam (Premium Tents) at Kumbh Sukrit"
      description={
        <>
          <p>
            Our <strong>Sanskriti Vann Mandapam (Premium Tents)</strong> offer elegant
            accommodation for two guests, combining modern comfort with traditional charm.
            Each tent is thoughtfully designed to provide a peaceful retreat during your
            spiritual journey at Magh Mela.
          </p>
          <p className="mt-4">
            Featuring two comfortable single beds, private bathroom facilities, and ample
            space for your belongings, these tents are perfect for couples, friends, or
            siblings traveling together. The premium construction ensures durability and
            comfort throughout your stay.
          </p>
          <p className="mt-4">
            Climate control, quality bedding, and tasteful decor create an inviting
            atmosphere where you can rest and rejuvenate between your daily activities at
            the Mela. The tents are strategically positioned to offer tranquility while
            maintaining easy access to all camp facilities.
          </p>
        </>
      }
      amenities={INCLUDED_AMENITIES}
      optionalServices={OPTIONAL_SERVICES}
    />
  );
};

// Package detail pages
export const mudhousePage: Page = {
  title: 'home:accommodation.mudhouse.title',
  subtitle: 'home:accommodation.mudhouse.description',
  path: PageRoutes.mudhouse,
  heroImage: images['mudhouse-hero-inner'],
  heroImageAlt: 'home:accommodation.mudhouse.title',
  readingTime: 5,
  maxWidth: true,
  content: <MudhouseContent />,
  linkedPages: (s) => [
    s.getPageOrThrow(PageRoutes.mudhouseDormitory),
    s.getPageOrThrow(PageRoutes.premiumTwinTent),
  ],
};

export const mudhouseDormitoryPage: Page = {
  title: 'home:accommodation.dormitory.title',
  subtitle: 'home:accommodation.dormitory.description',
  path: PageRoutes.mudhouseDormitory,
  heroImage: images['mudhouse-exterior-1'],
  heroImageAlt: 'home:accommodation.dormitory.title',
  readingTime: 4,
  maxWidth: true,
  content: <MudhouseDormitoryContent />,
  linkedPages: (s) => [
    s.getPageOrThrow(PageRoutes.mudhouse),
    s.getPageOrThrow(PageRoutes.premiumTwinTent),
  ],
};

export const premiumTwinTentPage: Page = {
  title: 'home:accommodation.premium-tent.title',
  subtitle: 'home:accommodation.premium-tent.description',
  path: PageRoutes.premiumTwinTent,
  heroImage: images['premium-twin-cottage-2025-external-4'],
  heroImageAlt: 'home:accommodation.premium-tent.title',
  readingTime: 5,
  maxWidth: true,
  content: <PremiumTwinTentContent />,
  linkedPages: (s) => [
    s.getPageOrThrow(PageRoutes.mudhouse),
    s.getPageOrThrow(PageRoutes.mudhouseDormitory),
  ],
};

interface PackagePreviewProps {
  path: string;
  readingTime: number;
  title: TranslationKey;
  subtitle?: TranslationKey;
  heroImage: string;
}

const PackagePreview: React.FC<PackagePreviewProps> = ({
  path,
  readingTime,
  title,
  subtitle,
  heroImage,
}) => {
  const { t } = useTranslation(['home', 'common']);

  return (
    <article className="group">
      <Link prefetch="intent" to={path}>
        <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-emerald-200">
          <div className="relative h-56 overflow-hidden">
            <img
              src={heroImage}
              alt={t(title)}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <span className="inline-block px-2.5 py-1 bg-emerald-600/90 text-white text-xs font-semibold uppercase tracking-wider rounded-full">
                {readingTime} {t('common:min-read')}
              </span>
            </div>
          </div>
          <div className="p-6">
            <h3 className="font-display text-emerald-800 group-hover:text-emerald-600 text-xl font-bold transition-colors">
              {t(title)}
            </h3>
            {subtitle && (
              <p className="mt-2 text-gray-600 text-sm line-clamp-2">{t(subtitle)}</p>
            )}
            <div className="mt-5 flex items-center text-emerald-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
              View Details &rarr;
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

// Main packages page that lists all accommodations
export const packagesPageChildren = [
  mudhouseDormitoryPage,
  premiumTwinTentPage,
  mudhousePage,
];

export const packagesPage: Page = {
  title: 'home:accommodation.title',
  subtitle: 'home:accommodation.offerings',
  path: PageRoutes.packages,
  heroImage: images['mudhouse-hero'],
  heroImageAlt: 'home:accommodation.title',
  readingTime: packagesPageChildren.reduce(
    (acc, { readingTime }) => acc + readingTime,
    0,
  ),
  maxWidth: true,
  content: (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {packagesPageChildren.map((page, index) => (
        <PackagePreview key={index} {...page} />
      ))}
    </div>
  ),
};
